<template>
    <v-dialog v-model="show"
              width="auto"
              persistent>
        <v-card width="600">
            <v-form v-model="valid" @submit.prevent="submit" :disabled="loading">
                <v-card-title class="d-flex justify-space-between align-center">
                    <div class="ps-2">Create Product</div>
                    <v-btn icon="mdi-close"
                           variant="text"
                           @click="show = false"></v-btn>
                </v-card-title>
                <v-card-subtitle>
                    Review the details from the job line below, then complete the required fields to save it as a customer product.
                </v-card-subtitle>
                <v-card-text v-if="line && line.product">
                    <v-list lines="one" density="compact">
                        <v-list-item title="Customer" :subtitle="contact ? contact.name : ''"></v-list-item>
                        <v-list-item title="Name" :subtitle="line.description"></v-list-item>
                        <v-list-item title="UOM" :subtitle="line.product.uomType"></v-list-item>
                        <v-list-item v-if="isLength" title="Length" :subtitle="line.length + ' MM'"></v-list-item>
                        <v-list-item v-if="isSqm" title="Width" :subtitle="line.width + ' MM'"></v-list-item>
                        <v-list-item v-if="isSqm" title="Height" :subtitle="line.height + ' MM'"></v-list-item>
                        <v-list-item title="Pricing Rate" :subtitle="'$' + numberWithCommas(line.uomMultiplier, 2) + ' ' + uomPerDisplay(line.product)"></v-list-item>
                    </v-list>
                    <hr class="my-2" />
                    <v-text-field v-model="code"
                                  :rules="[$rules.required]"
                                  label="Code *"></v-text-field>
                    <v-text-field v-if="isPeriphery"
                                  label="Periphery *"
                                  v-model.number="periphery"
                                  :rules="[$rules.required]"
                                  hint="Periphery of product in millimeters"
                                  persistent-hint
                                  type="number"
                                  suffix="MM"></v-text-field>
                    <v-checkbox v-if="!isUnit"
                                v-model="fixedSize"
                                hide-details
                                density="compact"
                                :label="isSqm ? 'Fixed size?' : 'Fixed length?'"></v-checkbox>
                    <v-alert density="compact"
                             v-if="errors"
                             type="warning"
                             class="my-2"
                             :title="errors.title">
                        <ul>
                            <li v-for="error in errors.errors" :key="error" v-html="error[0]"></li>
                        </ul>
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-btn :loading="loading"
                           type="submit"
                           :disabled="!saveValid"
                           flat
                           color="primary"
                           prepend-icon="mdi-floppy">SAVE</v-btn>
                    <v-btn flat class="ml-2" @click="show = false">CANCEL</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>
<script>
    import { encodeProductBasic } from '../../mixins/entityEncodeMixins';
    import { numberWithCommas } from "../../mixins/numberMixins";
    import { uomPerDisplay } from "../../mixins/productMixins";
    export default {
        name: "CreateProductFromLine",
        data: function () {
            return {
                loading: false,
                valid: false,
                errors: null,
                code: "",
                periphery: null,
                fixedSize: false
            };
        },
        props: {
            modelValue: {
                type: Boolean,
                default: false
            },
            line: {
                type: Object,
                default: null
            },
            contact: {
                type: Object,
                default: null
            }
        },
        emits: ['update:modelValue', 'created'],
        methods: {
            numberWithCommas,
            uomPerDisplay,
            submit() {
                if (!this.saveValid) {
                    return;
                }
                this.loading = true;
                this.errors = null;
                let product = {
                    type: this.$constants.products.TYPE_CUST,
                    name: this.line.description.trim(),
                    code: this.code.trim(),
                    contact: this.contact,
                    uomType: this.line.product.uomType,
                    fixedSize: !this.isUnit && this.fixedSize,
                    // Sizes are only stored against the product when it is fixed
                    length: this.fixedSize && this.isLength ? this.line.length : null,
                    width: this.fixedSize && this.isSqm ? this.line.width : null,
                    height: this.fixedSize && this.isSqm ? this.line.height : null,
                    periphery: this.isPeriphery ? this.periphery : null,
                    pricingRate: this.line.uomMultiplier
                };
                this.$api({
                    method: 'post',
                    url: this.$constants.entities.Product.CREATE,
                    data: encodeProductBasic(new FormData(), product)
                }).then(res => {
                    this.loading = false;
                    this.$emit('created', res.data);
                    this.show = false;
                }).catch(error => {
                    if (error.response && error.response.data && Array.isArray(error.response.data)) {
                        this.errors = error.response.data;
                    } else if (error.response && error.response.data && error.response.data.title) {
                        this.errors = { title: "Whoops", errors: [[error.response.data.title]] };
                    } else {
                        this.errors = { title: "Whoops", errors: [["There was an issue saving the product."]] };
                    }
                    this.loading = false;
                });
            }
        },
        computed: {
            show: {
                get() {
                    return this.modelValue;
                },
                set(value) {
                    this.$emit('update:modelValue', value);
                }
            },
            uomType() {
                return this.line && this.line.product ? this.line.product.uomType : null;
            },
            isSqm() {
                return this.uomType == this.$constants.products.UOM_SQM;
            },
            isPeriphery() {
                return this.uomType == this.$constants.products.UOM_PERI;
            },
            isLength() {
                return this.isPeriphery || this.uomType == this.$constants.products.UOM_LENG;
            },
            isUnit() {
                return this.uomType == this.$constants.products.UOM_UNIT;
            },
            saveValid() {
                if (!this.code || !this.code.trim()) {
                    return false;
                }
                if (this.isPeriphery && !this.periphery) {
                    return false;
                }
                return true;
            }
        },
        watch: {
            modelValue(value) {
                // Fresh inputs each time the modal is opened
                if (value) {
                    this.code = "";
                    this.periphery = null;
                    this.fixedSize = false;
                    this.errors = null;
                }
            }
        }
    };
</script>
